import { Card, Spin, Empty, Typography } from 'antd';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import moment from 'moment';

interface VoucherStatsChartProps {
  stats: Array<{ date: string; count: number }>;
  loading: boolean;
  error: string | null;
  siteName?: string;
}

const VoucherStatsChart: React.FC<VoucherStatsChartProps> = ({ stats, loading, error, siteName }) => {
  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <Typography style={{ marginTop: '16px', display: 'block' }}>Loading voucher statistics...</Typography>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Empty
            description={
              <Typography.Text type="danger">{error || 'Failed to load voucher statistics'}</Typography.Text>
            }
          />
        </div>
      </Card>
    );
  }

  // Format dates for display (e.g., "Jan 15" or "15 Jan")
  const formatDateForDisplay = (dateStr: string): string => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = moment(date).format('MMM');
    return `${day} ${month}`;
  };

  // Prepare chart data
  const categories = stats.map((stat) => formatDateForDisplay(stat.date));
  const seriesData = stats.map((stat) => stat.count);

  const chartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
        columnWidth: '55%',
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ['#304758'],
      },
    },
    xaxis: {
      categories: categories,
      title: {
        text: 'Date',
        style: {
          fontSize: '14px',
          fontWeight: 600,
        },
      },
    },
    yaxis: {
      title: {
        text: 'Vouchers Used',
        style: {
          fontSize: '14px',
          fontWeight: 600,
        },
      },
      labels: {
        formatter: (value: number) => {
          return Math.floor(value).toString();
        },
      },
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.25,
        gradientToColors: ['#3A77BC'],
        inverseColors: false,
        opacityFrom: 0.85,
        opacityTo: 0.85,
        stops: [0, 100],
      },
    },
    colors: ['#3A77BC'],
    tooltip: {
      y: {
        formatter: (value: number) => {
          return `${value} voucher${value !== 1 ? 's' : ''} used`;
        },
      },
    },
    grid: {
      borderColor: '#e0e0e0',
      strokeDashArray: 3,
    },
  };

  const chartSeries = [
    {
      name: 'Vouchers Used',
      data: seriesData,
    },
  ];

  return (
    <Card title={`Voucher Usage Statistics - Last 7 Days${siteName ? ` (${siteName})` : ''}`}>
      {stats.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Empty description="No voucher usage data available for the last 7 days." />
        </div>
      ) : (
        <ReactApexChart options={chartOptions} series={chartSeries} type="bar" height={350} />
      )}
    </Card>
  );
};

export default VoucherStatsChart;
